import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { trpc } from "~/utils/trpc";
import { allowedTables } from "@quake/isomorphic";

export type AllowedTableOption = {
  label: string;
  value: string;
  isCustomEntity?: boolean;
};

/**
 * Hook that combines static allowedTables with dynamically fetched custom entities
 * This allows any form or component to select from both predefined tables and custom entities
 *
 * @param excludeEntityId - Optional custom entity ID to exclude (prevents self-reference)
 * @returns Combined list of allowed tables including custom entities
 */
export function useAllowedTablesWithCustomEntities(excludeEntityId?: string): {
  allAllowedTables: AllowedTableOption[];
  isLoading: boolean;
} {
  // Fetch custom models to include them as allowed tables
  const { data: customModels, isLoading } = useQuery({
    ...trpc.customEntities.getCustomModels.queryOptions(),
    // Ensure the query is always enabled
    enabled: true,
  });

  // Combine allowedTables with custom entities
  const allAllowedTables = useMemo(() => {
    // Start with base allowed tables
    const baseTables = [...allowedTables];

    // If custom models aren't loaded yet, return just base tables
    if (!customModels || customModels.length === 0) {
      return baseTables;
    }

    // Add custom entities to the list
    const customEntityTables = customModels
      .filter((model) => model.id !== excludeEntityId) // Filter out excluded entity to prevent circular references
      .map((model) => ({
        label: model.p_name,
        value: `custom_entity:${model.id}`,
        isCustomEntity: true,
      }));

    return [...baseTables, ...customEntityTables];
  }, [customModels, excludeEntityId]);

  return { allAllowedTables, isLoading };
}
